import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, Subscription, combineLatest, forkJoin, tap } from 'rxjs';
import { IAlertResponse } from 'src/app/alert/models/alert.model';
import { AlertState } from 'src/app/alert/state/alert.state';
import { DueListState } from 'src/app/due-list/state/due-list.state';
import { NonConformanceApiService } from 'src/app/non-conformance-user/services/non-conformance-api.service';
import { NonConformanceStatus } from 'src/app/non-conformance-user/enums/non-conformance-status.enum';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { OrderStatus } from 'src/app/order-user/enums/order-status.enum';
import { OrderApiService } from 'src/app/order-user/services/order-api.service';
import { IOrderResponse } from 'src/app/order-user/models/order.model';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IDueListCounts } from 'src/app/due-list/models/due-list-counts.model';
import { IUserAccess } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MainTableFilterModulesEnum } from '../../enums/main-table-filter-modules.enum';
import { ApiResponse } from '../../models/api-response';
import { UiState } from '../../state/ui.state';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  alerts$: Observable<IAlertResponse[]> = this.store.select(AlertState.getAlerts);
  currentTenantId$: Observable<string> = this.store.select(TenantUserAccessState.currentTenantId);
  currentDepartmentId$: Observable<string> = this.store.select(
    TenantUserAccessState.currentDepartmentId
  );
  isProductOwner$: Observable<boolean | undefined> = this.store.select(
    TenantUserAccessState.isProductOwner
  );
  isDarkMode$: Observable<boolean> = this.store.select(UiState.isDarkMode);
  userAccess$: Observable<IUserAccess> = this.store.select(TenantUserAccessState.userAccess);

  isMobile: boolean = false;

  // Due List numbers
  expiredWarnings = 0;
  soonExpiringWarnings = 0;
  totalWarnings = 0;
  warningsLoaded = false;
  expiredCompetences = 0;
  soonExpiringCompetences = 0;
  competencesLoaded = false;
  userNameAndSurname: string;
  darkMode: boolean;

  // Alerts
  alertSum = 0;

  // Non-Conformances
  ncOpen: number;
  ncCompleted: number;
  ncStillOpenPercent: number;
  canReadNC = false;

  // Orders
  ordersSentToSupplier = 0;
  canReadOrders = false;

  isUserProductOwner = false;
  displayDashboard = false;

  private tenantId: string;
  private departmentId: string;
  private subscription = new Subscription();

  constructor(
    private store: Store,
    private nonConformanceApiService: NonConformanceApiService,
    private orderApiService: OrderApiService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver.observe(['(max-width: 670px)']).subscribe((result) => {
      this.isMobile = result.matches;
    });
    this.isDarkMode();
    this.checkIfUserIsProductOwner();
    this.listenToTenantOrDepartmentChanges();
    this.getDueListCounts();
    this.getAlerts();
    this.getUserNameAndSurname();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private listenToTenantOrDepartmentChanges(): void {
    this.subscription.add(
      combineLatest([this.currentTenantId$, this.currentDepartmentId$]).subscribe(
        (result: string[]) => {
          const tenantId = result[0];
          const depId = result[1];

          if (tenantId !== undefined) {
            this.displayDashboard = true;
          }

          if (this.tenantId === undefined) {
            this.tenantId = tenantId;
          }
          if (this.departmentId === undefined) {
            this.departmentId = depId;
          }
          if (
            (depId !== ALL_DEPS_TOKEN && this.tenantId !== tenantId) ||
            (depId !== ALL_DEPS_TOKEN && this.departmentId !== depId)
          ) {
            this.tenantId = tenantId;
            this.departmentId = depId;
            if (this.isUserProductOwner) {
              this.getNonConformances();
              this.getOrders();
            } else {
              if (this.canReadOrders) {
                this.getOrders();
              }
              if (this.canReadNC) {
                this.getNonConformances();
              }
            }
          }
        }
      )
    );
  }

  getDueListCounts(): void {
    this.subscription.add(
      this.store
        .select(DueListState.getDueListCounts)
        .pipe(
          tap((counts: IDueListCounts | undefined) => {
            if (counts) {
              this.expiredWarnings = counts.expiredWarnings;
              this.soonExpiringWarnings = counts.expiringWarnings;
              this.totalWarnings = counts.expiredWarnings + counts.expiringWarnings;
              this.expiredCompetences = counts.expiredCompetences;
              this.soonExpiringCompetences = counts.expiringCompetences;
              if (this.expiredWarnings > 0) {
                setTimeout(() => {
                  this.warningsLoaded = true;
                });
              } else {
                this.warningsLoaded = false;
              }
              if (this.expiredCompetences > 0) {
                setTimeout(() => {
                  this.competencesLoaded = true;
                });
              } else {
                this.competencesLoaded = false;
              }
            }
          })
        )
        .subscribe()
    );
  }

  private getAlerts(): void {
    this.subscription.add(
      this.alerts$.subscribe((alerts: IAlertResponse[]) => {
        this.alertSum = alerts.length ?? 0;
      })
    );
  }

  private getNonConformances(): void {
    const tempFilterObj = new MainTableFilterModel(MainTableFilterModulesEnum.NonConformances);
    tempFilterObj.pageIndex = 0;
    // eslint-disable-next-line no-magic-numbers
    tempFilterObj.pageSize = 10000;
    const ncStateOpen = NonConformanceStatus.Open;
    const ncStateCompleted = NonConformanceStatus.Completed;

    this.subscription.add(
      forkJoin([
        this.nonConformanceApiService.getAllNonConformancesByState(ncStateOpen, tempFilterObj),
        this.nonConformanceApiService.getAllNonConformancesByState(ncStateCompleted, tempFilterObj),
      ]).subscribe(([openResponse, completedResponse]) => {
        this.ncOpen = openResponse.data?.length ?? 0;
        this.ncCompleted = completedResponse.data?.length ?? 0;

        const total = this.ncOpen + this.ncCompleted;

        this.ncStillOpenPercent = total > 0 ? (this.ncOpen / total) * 100 : 0;
      })
    );
  }

  private getOrders(): void {
    const tempFilterObj = new MainTableFilterModel(MainTableFilterModulesEnum.Orders);
    tempFilterObj.pageIndex = 0;
    // eslint-disable-next-line no-magic-numbers
    tempFilterObj.pageSize = 10000;
    const orderStageSentToSupplier = OrderStatus.SentToSupplier;
    this.subscription.add(
      this.orderApiService
        .getOrders(orderStageSentToSupplier, tempFilterObj)
        .subscribe((apiResponse: ApiResponse<IOrderResponse[]>) => {
          this.ordersSentToSupplier = apiResponse.data?.length ?? 0;
        })
    );
  }

  private checkIfUserIsProductOwner(): void {
    this.subscription.add(
      this.isProductOwner$.subscribe((productOwner: boolean | undefined) => {
        if (productOwner) {
          this.getOrders();
          this.getNonConformances();
          this.isUserProductOwner = true;
        } else {
          this.checkModulePrivileges();
          this.isUserProductOwner = false;
        }
      })
    );
  }

  private checkModulePrivileges(): void {
    this.subscription.add(
      this.store
        .select(TenantUserAccessState.getPrivilegesFlat)
        .subscribe((privileges: string[]) => {
          if (privileges.includes('ORDER_READ')) {
            this.canReadOrders = true;
            this.getOrders();
          } else {
            this.canReadOrders = false;
            this.ordersSentToSupplier = 0;
          }
          if (privileges.includes('NON-CONFORMANCE_READ')) {
            this.canReadNC = true;
            this.getNonConformances();
          } else {
            this.canReadNC = false;
            this.ncOpen = 0;
            this.ncCompleted = 0;
            this.ncStillOpenPercent = 0;
          }
        })
    );
  }

  private getUserNameAndSurname(): void {
    this.subscription.add(
      this.userAccess$.subscribe((userAccess: IUserAccess) => {
        if (userAccess?.name) {
          this.userNameAndSurname = userAccess.name;
        }
      })
    );
  }

  private isDarkMode(): void {
    this.subscription.add(
      this.isDarkMode$.subscribe((darkMode: boolean) => {
        this.darkMode = darkMode;
      })
    );
  }
}
